import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { getBlockColor, getEmissiveColor } from './CyanMaterial';

// Shared geometry - created once, reused for all blocks
const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = BLOCK_SIZE * 0.75;
const sharedBlockGeometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH);
const sharedGlowGeometry = new THREE.BoxGeometry(BLOCK_SIZE * 1.25, BLOCK_SIZE * 1.25, BLOCK_DEPTH * 0.6);
const sharedCoreGeometry = new THREE.SphereGeometry(0.25, 12, 12);
const sharedDiagonalBarGeometry = new THREE.PlaneGeometry(BLOCK_SIZE * 0.15, BLOCK_SIZE * 1.4);
const sharedEdgeGeometry = new THREE.EdgesGeometry(new THREE.BoxGeometry(BLOCK_SIZE * 1.02, BLOCK_SIZE * 1.02, BLOCK_DEPTH * 1.02));

const IceBlock3D = ({
  position,
  colorClass,
  isGhost = false,
  isActive = false,
  animate = true,
  scale = 1
}) => {
  const groupRef = useRef();
  const coreRef = useRef();
  const haloRef = useRef();
  const diagonalBarRef = useRef();
  const edgeRef = useRef();
  const phaseRef = useRef(Math.random() * Math.PI * 2);

  const baseColor = getBlockColor(colorClass);
  const emissiveColor = getEmissiveColor(colorClass);

  // Single consolidated useFrame for all animations
  useFrame((state) => {
    const time = state.clock.elapsedTime;

    // Scale pulse for active pieces
    if (groupRef.current && animate && isActive) {
      const pulse = Math.sin(time * 2.5) * 0.02 + 1;
      groupRef.current.scale.setScalar(pulse);
    }

    if (!isGhost) {
      // Core glow pulse
      if (coreRef.current?.material) {
        const corePulse = isActive
          ? 0.6 + Math.sin(time * 3) * 0.2 + Math.sin(time * 7) * 0.1
          : 0.4 + Math.sin(time * 1.5) * 0.15;
        coreRef.current.material.opacity = corePulse;
        coreRef.current.material.emissiveIntensity = corePulse * 1.5;
      }

      // Halo pulse
      if (haloRef.current?.material) {
        const haloPulse = isActive
          ? 0.5 + Math.sin(time * 2.5) * 0.2
          : 0.3 + Math.sin(time * 1.2) * 0.1;
        haloRef.current.material.opacity = haloPulse;
      }

      // Edge highlight
      if (edgeRef.current?.material) {
        const edgeIntensity = isActive
          ? 0.9 + Math.sin(time * 4) * 0.1
          : 0.6 + Math.sin(time * 2) * 0.1;
        edgeRef.current.material.opacity = edgeIntensity;
      }
    }
  });

  // Premium crystal material
  const material = useMemo(() => {
    if (isGhost) {
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(baseColor),
        emissive: new THREE.Color(baseColor),
        emissiveIntensity: 0.5,
        metalness: 0.3,
        roughness: 0.2,
        opacity: 0.35,
        transparent: true,
        side: THREE.DoubleSide,
      });
    }

    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(baseColor),
      emissive: new THREE.Color(emissiveColor),
      emissiveIntensity: isActive ? 1.0 : 0.75,
      metalness: 0.85,
      roughness: 0.05,
      side: THREE.DoubleSide,
    });
  }, [baseColor, emissiveColor, isGhost, isActive]);

  const blockScale = scale;

  return (
    <group position={position} ref={groupRef} scale={blockScale}>
      {/* Main crystal block body */}
      <mesh
        geometry={sharedBlockGeometry}
        material={material}
        castShadow={!isGhost}
        receiveShadow={!isGhost}
      />

      {/* Inner core glow */}
      {!isGhost && (
        <mesh ref={coreRef} geometry={sharedCoreGeometry}>
          <meshStandardMaterial
            color="#00FFFF"
            emissive="#00FFFF"
            emissiveIntensity={0.8}
            transparent
            opacity={0.5}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Outer glow halo */}
      {!isGhost && (
        <mesh ref={haloRef} geometry={sharedGlowGeometry}>
          <meshBasicMaterial
            color="#00E5E5"
            transparent
            opacity={0.35}
            side={THREE.BackSide}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Diagonal light bar (top-left to bottom-right) */}
      {!isGhost && (
        <mesh position={[0, 0, 0.34]} rotation={[0, 0, -Math.PI / 4]} geometry={sharedDiagonalBarGeometry}>
          <meshBasicMaterial
            color="#FFFFFF"
            transparent
            opacity={0.45}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Crystal edge highlight */}
      {!isGhost && (
        <lineSegments ref={edgeRef} geometry={sharedEdgeGeometry}>
          <lineBasicMaterial
            color="#80FFFF"
            linewidth={2}
            opacity={0.7}
            transparent
            blending={THREE.AdditiveBlending}
          />
        </lineSegments>
      )}
    </group>
  );
};

export default IceBlock3D;
